//#region redux
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ReceiptFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { DimensionEnums } from "models/enums";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
//#endregion
//#region components
import { useDimensionMaps } from "components/common";
import { costForwardingValidationFields } from "components/costForwardingMarking";
//#endregion
import * as Yup from "yup";

function useReceiptDraftFormValidations() {
  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Receipt);
  const receiptDraftValidationSchemeProperties = (isSoftValidation?: boolean) => {
    const softValidationScheme = {
      ...costForwardingValidationFields,
      filesToUpload: Yup.array().of(
        Yup.object<ReceiptFormTypes.ReceiptUploadedFile>({
          id: Yup.mixed(),
          contentType: Yup.string(),
          fileName: Yup.string(),
          fileSize: Yup.number(),
          data: Yup.string(),
          isDocumentDraft: Yup.bool(),
        })
      ),
      amount: Yup.number(),
      currency: Yup.string(),
      receiptCategory: Yup.string(),
      paymentMethod: Yup.string(),
      dimensions: Yup.array().of(
        Yup.object<WintSuperkollAPIModel.DimensionData>({
          dimensionId: Yup.string(),
          amount: Yup.number(),
        })
      ),
      receiptPersons: Yup.array()
        .of(
          Yup.object<ReceiptFormTypes.ReceiptPerson>({
            id: Yup.number(),
            name: Yup.string().required(TranslationKeys.Validations.Required),
            company: Yup.string().required(TranslationKeys.Validations.Required),
          })
        )
        .when(
          FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptCategoryType,
          (
            [value]: [WintSuperkollAPIModel.ReceiptCategoryType],
            schema: ArraySchema<WintSuperkollAPIModel.ReceiptPerson>
          ) =>
            ((value === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
              value === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
              schema.required(TranslationKeys.Validations.Required)) ||
            schema
        ),
      receiptPurpose: Yup.string().when(
        FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptCategoryType,
        ([value], schema: Yup.StringSchema) =>
          ((value === WintSuperkollAPIModel.ReceiptCategoryType.Representation ||
            value === WintSuperkollAPIModel.ReceiptCategoryType.EmployeeRepresentation) &&
            schema.required(TranslationKeys.Validations.Required)) ||
          schema
      ),
      otherExplanation: Yup.string().when(
        FormFieldConstants.Receipt.ReceiptDraftTodo.ReceiptCategoryType,
        ([value], schema: Yup.StringSchema) =>
          (value === WintSuperkollAPIModel.ReceiptCategoryType.Other &&
            schema.required(TranslationKeys.Validations.Required)) ||
          schema
      ),
    };

    if (isSoftValidation) return softValidationScheme;

    return {
      ...softValidationScheme,
      filesToUpload: softValidationScheme.filesToUpload
        .required(TranslationKeys.Validations.Required)
        .min(1, TranslationKeys.Validations.Required),
      amount: softValidationScheme.amount.required(TranslationKeys.Validations.Required),
      currency: softValidationScheme.currency.required(TranslationKeys.Validations.Required),
      receiptCategory: softValidationScheme.receiptCategory.required(TranslationKeys.Validations.Required),
      paymentMethod: softValidationScheme.paymentMethod.required(TranslationKeys.Validations.Required),
      dimensions: softValidationScheme.dimensions
        // eslint-disable-next-line no-empty-pattern
        .when([], ([], schema: ArraySchema<WintSuperkollAPIModel.DimensionData>) =>
          forcedDimensionMap.length > 0 ? schema.required("") : schema
        )
        .checkForcedDimensions(forcedDimensionMap)
        .when(
          FormFieldConstants.Receipt.ReceiptDraftTodo.Amount,
          ([amount]: [number], schema: ArraySchema<WintSuperkollAPIModel.DimensionData>) =>
            schema.validateDimensionAmounts(dimensions, amount)
        ),
    };
  };

  const receiptDraftValidationScheme = (isSoftValidation?: boolean) => {
    return Yup.object<ReceiptFormTypes.ReceiptDraftTodo>(receiptDraftValidationSchemeProperties(isSoftValidation));
  };

  return { receiptDraftValidationScheme, receiptDraftValidationSchemeProperties };
}

export default useReceiptDraftFormValidations;
